import Breadcrumb from "components/Common/Breadcrumb"
import LoadingButton from "components/Common/LoadingButton"
import { axiosbaseurl } from "configure"
import React, { useEffect, useState, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Container, CardBody, Card, Row, Col, Button } from "reactstrap"
import Select from "react-select"
import { customStyles } from "helpers/select-styles"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import swal from "sweetalert"
import moment from "moment"
import { rangesOverlap, toMinutes } from "helpers/common_helper"
import secureLocalStorage from "react-secure-storage"
import { fixedIds } from "app_config"

const AddMeeting = () => {
  const navigate = useNavigate()
  const { checkAccess } = usePermissionData()
  const module_id = 30

  const [date, setDate] = useState(moment().format("YYYY-MM-DD"))
  const [attendees, setAttendees] = useState([])
  const [missed, setMissed] = useState([])
  const [title, setTitle] = useState("")
  const [fromTime, setFromTime] = useState("")
  const [toTime, setToTime] = useState("")

  const [workTypeOptions, setWorkTypeOptions] = useState([])
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [clientOptions, setClientOptions] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [serviceOptions, setServiceOptions] = useState([])
  const [allTaskOptions, setAllTaskOptions] = useState([])

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const [entries, setEntries] = useState([
    {
      id: 1,
      client: null,
      service: null,
      task: null,
      status: null,
      workType: null,
      inTime: "",
      outTime: "",
      agenda: "",
    },
  ])

  // ── Load all dropdown options in one go ───────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        const [wtRes, empRes, cliRes, taskRes, statusRes, svcRes] =
          await Promise.all([
            axiosbaseurl.get("/work-type/active"),
            axiosbaseurl.get("/employee-master/active"),
            axiosbaseurl.get("/client"),
            axiosbaseurl.get("/tasks/all/normal"),
            axiosbaseurl.get("/status-master/active"),
            axiosbaseurl.get("/service-master/active"),
          ])

        if (wtRes.data.success) {
          setWorkTypeOptions(
            wtRes.data.data
              .filter(wt => wt.isMeeting)
              .map(wt => ({ value: wt.id, label: wt.workType }))
          )
        }

        if (empRes.data.success) {
          setEmployeeOptions(
            empRes.data.data.map(e => ({
              value: e.id,
              label: `${e.name} ${e.lastName}`,
            }))
          )
        }

        // serviceNameId: "[{\"id\":1,\"name\":\"Self-Assessment Tax Return\",\"isChecked\":true,\"price\":230,\"discount\":5,\"total\":218.5,\"clientType\":1},{\"id\":9,\"name\":\"Confirmation Statement\",\"isChecked\":true,\"price\":400,\"discount\":10,\"total\":360,\"clientType\":1},{\"id\":13,\"name\":\"CIS Return\",\"isChecked\":true,\"price\":100,\"discount\":20,\"total\":80,\"clientType\":0},{\"id\":14,\"name\":\"Payroll\",\"isChecked\":true,\"price\":200,\"discount\":10,\"total\":180,\"clientType\":0}]"

        if (cliRes.data.success) {
          // Store clientTypeId so EntryRow can filter services per client
          setClientOptions(
            cliRes.data.data
              .filter(item => Number(item.clientStatusId) === 1)
              .map(c => ({
                value: c.id,
                label: `${c.clientCode || ""} - ${c.clientName}`,
                clientTypeId: c.clientTypeId,
                services: c.serviceNameId
                  ? JSON.parse(JSON.parse(c.serviceNameId))
                  : [],
              }))
          )
        }

        if (taskRes.data.success) {
          setAllTaskOptions(
            taskRes.data.data.map(t => ({
              value: t.id,
              label: `${t.title} - ${moment(t.startDate).format("DD/MM/YYYY")}`,
              clientId: t.clientId,
              serviceId: t.serviceNameId,
            }))
          )
        }

        if (statusRes.data.success) {
          setStatusOptions(
            statusRes.data.data
              .slice()
              .sort((a, b) => a.statusName.localeCompare(b.statusName))
              .filter(item => Number(item.id) !== Number(fixedIds.HMRCStatus))
              .map(t => ({ value: t.id, label: t.statusName }))
          )
        }

        if (svcRes.data.success) {
          // Keep clientTypeId on each service option for per-client filtering
          setServiceOptions(
            svcRes.data.data
              .slice()
              .sort((a, b) => a.serviceName.localeCompare(b.serviceName))
              .map(s => ({
                value: s.id,
                label: s.serviceName,
                clientTypeId: s.clientTypeId,
                category: s.serviceCategory,
              }))
          )
        }
      } catch (err) {
        console.error("Data load failed:", err)
      }
    }
    loadData()
  }, [])

  console.log("statusOptions", statusOptions)

  const availableEmployees = useMemo(() => {
    const taken = new Set([
      ...attendees.map(a => a.value),
      ...missed.map(m => m.value),
    ])
    return employeeOptions.filter(emp => !taken.has(emp.value))
  }, [employeeOptions, attendees, missed])

  const addEntry = () => {
    const newId = Math.max(0, ...entries.map(e => e.id)) + 1
    setEntries(prev => [
      ...prev,
      {
        id: newId,
        client: null,
        service: null,
        task: null,
        status: null,
        workType: null,
        inTime: "",
        outTime: "",
        agenda: "",
      },
    ])
  }

  const removeEntry = id => {
    if (entries.length === 1) return
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  const updateEntry = useCallback((id, field, value) => {
    setEntries(prev =>
      prev.map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    )
  }, [])

  const getTaskOptionsForClient = useCallback(
    (clientValue, serviceValue) => {
      if (!clientValue) return []
      return allTaskOptions.filter(t => {
        const matchesClient = t.clientId === clientValue
        const matchesService = serviceValue
          ? t.serviceId === serviceValue
          : true
        return matchesClient && matchesService
      })
    },
    [allTaskOptions]
  )

  // ── Helper: format minutes as "Xh Ym" ────────────────────────────────────
  const formatMinutes = mins => {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (h > 0 && m > 0) return `${h}h ${m}m`
    if (h > 0) return `${h}h`
    return `${m}m`
  }

  const validate = () => {
    const tempErrors = {}

    if (!title) tempErrors.title = "Meeting title is required"

    if (attendees.length === 0)
      tempErrors.attendees = "At least one attendee is required"

    const meetingStart = toMinutes(fromTime)
    const meetingEnd = toMinutes(toTime)
    const hasMeetingRange = meetingStart !== null && meetingEnd !== null

    entries.forEach(entry => {
      if (!entry.client)
        tempErrors[`entry_${entry.id}_client`] = "Client is required"
      if (!entry.workType)
        tempErrors[`entry_${entry.id}_workType`] = "Work type is required"
      if (!entry.service)
        tempErrors[`entry_${entry.id}_service`] = "Service is required"

      // ── In Time ──────────────────────────────────────────────────────────
      if (!entry.inTime) {
        tempErrors[`entry_${entry.id}_inTime`] = "In time is required"
      } else if (hasMeetingRange) {
        const entryIn = toMinutes(entry.inTime)
        if (entryIn < meetingStart) {
          tempErrors[
            `entry_${entry.id}_inTime`
          ] = `Cannot be before meeting start (${fromTime})`
        } else if (entryIn >= meetingEnd) {
          tempErrors[
            `entry_${entry.id}_inTime`
          ] = `Cannot be at or after meeting end (${toTime})`
        }
      }

      // ── Out Time ─────────────────────────────────────────────────────────
      if (!entry.outTime) {
        tempErrors[`entry_${entry.id}_outTime`] = "Out time is required"
      } else if (hasMeetingRange) {
        const entryOut = toMinutes(entry.outTime)
        if (entryOut > meetingEnd) {
          tempErrors[
            `entry_${entry.id}_outTime`
          ] = `Cannot exceed meeting end (${toTime})`
        } else if (entryOut <= meetingStart) {
          tempErrors[
            `entry_${entry.id}_outTime`
          ] = `Cannot be at or before meeting start (${fromTime})`
        }
      }

      // ── In-entry cross-check ──────────────────────────────────────────────
      if (
        entry.inTime &&
        entry.outTime &&
        !tempErrors[`entry_${entry.id}_inTime`] &&
        !tempErrors[`entry_${entry.id}_outTime`]
      ) {
        if (toMinutes(entry.inTime) >= toMinutes(entry.outTime)) {
          tempErrors[`entry_${entry.id}_outTime`] =
            "Out time must be after in time"
        }
      }
    })

    // ── Overlap check across entries ──────────────────────────────────────
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = entries[i],
          b = entries[j]
        if (
          rangesOverlap(a.inTime, a.outTime, b.inTime, b.outTime) &&
          !tempErrors[`entry_${a.id}_inTime`] &&
          !tempErrors[`entry_${a.id}_outTime`] &&
          !tempErrors[`entry_${b.id}_inTime`] &&
          !tempErrors[`entry_${b.id}_outTime`]
        ) {
          tempErrors[`entry_${a.id}_outTime`] = `Entry ${
            i + 1
          } overlaps with Entry ${j + 1} (${b.inTime}–${b.outTime})`
          tempErrors[`entry_${b.id}_inTime`] = `Entry ${
            j + 1
          } overlaps with Entry ${i + 1} (${a.inTime}–${a.outTime})`
        }
      }
    }

    setErrors(tempErrors)
    if (Object.keys(tempErrors).length > 0) return false

    // ── Total duration check ──────────────────────────────────────────────
    if (hasMeetingRange) {
      const totalMeetingMins = meetingEnd - meetingStart

      const totalEntryMins = entries.reduce((sum, entry) => {
        const inMin = toMinutes(entry.inTime)
        const outMin = toMinutes(entry.outTime)
        if (inMin !== null && outMin !== null && outMin > inMin) {
          return sum + (outMin - inMin)
        }
        return sum
      }, 0)

      if (totalEntryMins !== totalMeetingMins) {
        const meetingDuration = formatMinutes(totalMeetingMins)
        const entriesDuration = formatMinutes(totalEntryMins)
        const diff = Math.abs(totalMeetingMins - totalEntryMins)
        const isShort = totalEntryMins < totalMeetingMins

        swal({
          title: "Duration Mismatch",
          text: `Meeting duration is ${meetingDuration}, but your entries total ${entriesDuration}. You are ${
            isShort ? "short by" : "over by"
          } ${formatMinutes(
            diff
          )}. Please adjust your entry times so they add up to ${meetingDuration}.`,
          icon: "warning",
          button: "OK",
        })
        return false
      }
    }

    return true
  }

  const loggedInUser = secureLocalStorage.getItem("authUser")

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validate()) return

    const entriesArray = entries.map(entry => ({
      id: entry.id,
      clientId: entry.client?.value ?? entry.client,
      taskId: entry.task?.value ?? entry.task ?? null,
      serviceId: entry.service?.value ?? entry.service ?? null,
      statusId: entry.status?.value ?? entry.status ?? null,
      date,
      workTypeId: entry.workType?.value ?? entry.workType,
      inTime: entry.inTime,
      outTime: entry.outTime,
      agenda: entry.agenda.trim() || null,
    }))

    let attendis = null

    if (attendees.length) {
      let attendeeIds = attendees.map(a => a.value)

      if (
        loggedInUser.id != 1 &&
        !attendeeIds.includes(loggedInUser.employeeId)
      ) {
        attendeeIds = [...attendeeIds, loggedInUser.employeeId]
      }

      attendis = JSON.stringify(attendeeIds)
    }

    const payload = {
      meetingTitle: title,
      fromTime,
      toTime,
      attendees: attendis,
      missed: missed.length ? JSON.stringify(missed.map(m => m.value)) : null,
      entries: JSON.stringify(entriesArray),
    }

    try {
      setLoading(true)
      const res = await axiosbaseurl.post("/meeting", payload)
      if (res.data.success) {
        swal("Success", "Meeting created successfully", "success")
        navigate(-1)
      }
    } catch (err) {
      console.error(err)
      swal(
        "Error",
        err.response?.data?.message || "Failed to save meeting",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-content mb-5">
      <Container fluid>
        <Breadcrumb title="Dashboard" breadcrumbItem="Add Meeting" />

        <Card>
          <CardBody>
            <PermittedComponent isAccess={checkAccess(module_id, "add")}>
              <form onSubmit={handleSubmit}>
                <div className="text-end mb-4">
                  <LoadingButton loading={loading} btnText="Save Meeting" />
                  <button
                    type="button"
                    className="btn btn-secondary bg-dark ms-3"
                    onClick={() => navigate(-1)}
                  >
                    <i className="fas fa-arrow-left me-2" />
                    Back
                  </button>
                </div>

                <hr className="mb-2" />

                <Row className="mb-3 g-4">
                  <Col md={8}>
                    <label className="form-label">
                      Meeting Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                    />
                    {errors.title && (
                      <small className="text-danger d-block">
                        {errors.title}
                      </small>
                    )}
                  </Col>

                  <Col md={2}>
                    <label className="form-label">From Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={fromTime}
                      onChange={e => setFromTime(e.target.value)}
                    />
                  </Col>

                  <Col md={2}>
                    <label className="form-label">To Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={toTime}
                      onChange={e => setToTime(e.target.value)}
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label">
                      Attendees (Present) <span className="text-danger">*</span>
                    </label>
                    <Select
                      isMulti
                      value={attendees}
                      onChange={setAttendees}
                      options={availableEmployees}
                      styles={customStyles}
                      placeholder="Select who attended..."
                      closeMenuOnSelect={false}
                    />
                    {errors.attendees && (
                      <small className="text-danger d-block">
                        {errors.attendees}
                      </small>
                    )}
                  </Col>

                  <Col md={6}>
                    <label className="form-label">Missed / Absent</label>
                    <Select
                      isMulti
                      value={missed}
                      onChange={setMissed}
                      options={availableEmployees}
                      styles={customStyles}
                      placeholder="Select who missed..."
                      closeMenuOnSelect={false}
                    />
                  </Col>
                </Row>

                <div className="d-flex w-100 justify-content-end mb-3 align-items-center">
                  <Button color="primary" onClick={addEntry}>
                    + Add Another Entry
                  </Button>
                </div>

                {entries.map(entry => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    clientOptions={clientOptions}
                    workTypeOptions={workTypeOptions}
                    serviceOptions={serviceOptions}
                    statusOptions={statusOptions}
                    taskOptions={getTaskOptionsForClient(
                      entry.client?.value,
                      entry.service?.value
                    )}
                    errors={errors}
                    updateEntry={updateEntry}
                    removeEntry={removeEntry}
                    showRemove={entries.length > 1}
                    date={date}
                    fromTime={fromTime}
                    toTime={toTime}
                  />
                ))}
              </form>
            </PermittedComponent>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

// ─── EntryRow ─────────────────────────────────────────────────────────────────
const EntryRow = React.memo(
  ({
    entry,
    clientOptions,
    workTypeOptions,
    taskOptions,
    serviceOptions,
    statusOptions,
    errors,
    date,
    updateEntry,
    removeEntry,
    showRemove,
    fromTime,
    toTime,
  }) => {
    const filteredServices = useMemo(() => {
      if (!entry.client) return serviceOptions

      const clientOwnedServiceIds = entry.client.services?.map(s => s.id) ?? []

      return serviceOptions.filter(
        svc =>
          clientOwnedServiceIds.includes(svc.value) ||
          ((svc.category === "Non-Billiable and Non-scheduled" ||
            svc.category === "Non-Billiable and Scheduled") &&
            (svc.clientTypeId === entry.client.clientTypeId ||
              svc.clientTypeId === 0))
      )
    }, [entry.client, serviceOptions])

    // When client changes: reset all client-dependent fields
    const handleClientChange = useCallback(
      opt => {
        updateEntry(entry.id, "client", opt)
        updateEntry(entry.id, "task", null)
        updateEntry(entry.id, "service", null)
        updateEntry(entry.id, "status", null)
      },
      [entry.id, updateEntry]
    )
    const handleTaskChange = useCallback(
      opt => {
        updateEntry(entry.id, "task", opt)
        if (!opt) updateEntry(entry.id, "status", null)
      },
      [entry.id, updateEntry]
    )
    const handleServiceChange = useCallback(
      opt => {
        updateEntry(entry.id, "service", opt)
        updateEntry(entry.id, "task", null) // clear task when service changes
        updateEntry(entry.id, "status", null) // clear status too
      },
      [entry.id, updateEntry]
    )
    const handleStatusChange = useCallback(
      opt => updateEntry(entry.id, "status", opt),
      [entry.id, updateEntry]
    )
    const handleWorkTypeChange = useCallback(
      opt => updateEntry(entry.id, "workType", opt),
      [entry.id, updateEntry]
    )
    const handleInTimeChange = useCallback(
      e => updateEntry(entry.id, "inTime", e.target.value),
      [entry.id, updateEntry]
    )
    const handleOutTimeChange = useCallback(
      e => updateEntry(entry.id, "outTime", e.target.value),
      [entry.id, updateEntry]
    )
    const handleAgendaChange = useCallback(
      e => updateEntry(entry.id, "agenda", e.target.value),
      [entry.id, updateEntry]
    )
    const handleRemove = useCallback(
      () => removeEntry(entry.id),
      [entry.id, removeEntry]
    )

    const rangeHint =
      fromTime && toTime ? (
        <small className="text-muted">{`Allowed: ${fromTime} – ${toTime}`}</small>
      ) : null

    return (
      <div className="border p-4 mb-4 bg-light rounded position-relative">
        <Row className="g-3">
          {/* ── Client ── */}
          <Col md={4}>
            <label className="form-label">
              Client <span className="text-danger">*</span>
            </label>
            <Select
              value={entry.client}
              onChange={handleClientChange}
              options={clientOptions}
              styles={customStyles}
              placeholder="Select client"
            />
            {errors[`entry_${entry.id}_client`] && (
              <small className="text-danger">
                {errors[`entry_${entry.id}_client`]}
              </small>
            )}
          </Col>

          {/* ── Service (filtered by client's clientTypeId) ── */}
          <Col md={4}>
            <label className="form-label">
              Service <span className="text-danger">*</span>
            </label>
            <Select
              value={entry.service}
              onChange={handleServiceChange}
              options={filteredServices}
              styles={customStyles}
              isClearable
              isDisabled={!entry.client}
              placeholder={
                entry.client ? "Select service..." : "Select client first"
              }
            />
            {errors[`entry_${entry.id}_service`] && (
              <small className="text-danger">
                {errors[`entry_${entry.id}_service`]}
              </small>
            )}
          </Col>

          {/* ── Task (filtered by clientId) ── */}
          <Col md={4}>
            <label className="form-label">Related Task (optional)</label>
            <Select
              value={entry.task}
              onChange={handleTaskChange}
              options={taskOptions}
              styles={customStyles}
              isClearable
              isDisabled={!entry.client}
              placeholder={
                entry.client ? "Select task..." : "Select client first"
              }
            />
          </Col>

          {/* ── Task Status ── */}
          <Col md={3}>
            <label className="form-label">Task Status (optional)</label>
            <Select
              value={entry.status}
              onChange={handleStatusChange}
              options={statusOptions}
              styles={customStyles}
              isClearable
              isDisabled={!entry.client || !entry.task}
              placeholder={
                !entry.client
                  ? "Select client first"
                  : !entry.task
                  ? "Select task first"
                  : "Select status..."
              }
            />
          </Col>

          {/* ── Work Type ── */}
          <Col md={3}>
            <label className="form-label">
              Work Type <span className="text-danger">*</span>
            </label>
            <Select
              value={entry.workType}
              onChange={handleWorkTypeChange}
              options={workTypeOptions}
              styles={customStyles}
              placeholder="Select work type"
            />
            {errors[`entry_${entry.id}_workType`] && (
              <small className="text-danger">
                {errors[`entry_${entry.id}_workType`]}
              </small>
            )}
          </Col>

          {/* ── Date ── */}
          <Col md={2}>
            <label className="form-label">Date</label>
            <input type="date" className="form-control" value={date} disabled />
          </Col>

          {/* ── In Time ── */}
          <Col md={2}>
            <label className="form-label">
              In Time <span className="text-danger">*</span>
            </label>
            <input
              type="time"
              className="form-control"
              value={entry.inTime}
              min={fromTime || undefined}
              max={toTime || undefined}
              onChange={handleInTimeChange}
            />
            {rangeHint}
            {errors[`entry_${entry.id}_inTime`] && (
              <small className="text-danger d-block">
                {errors[`entry_${entry.id}_inTime`]}
              </small>
            )}
          </Col>

          {/* ── Out Time ── */}
          <Col md={2}>
            <label className="form-label">
              Out Time <span className="text-danger">*</span>
            </label>
            <input
              type="time"
              className="form-control"
              value={entry.outTime}
              min={fromTime || undefined}
              max={toTime || undefined}
              onChange={handleOutTimeChange}
            />
            {rangeHint}
            {errors[`entry_${entry.id}_outTime`] && (
              <small className="text-danger d-block">
                {errors[`entry_${entry.id}_outTime`]}
              </small>
            )}
          </Col>

          {/* ── Agenda ── */}
          <Col md={12}>
            <label className="form-label">
              Agenda / Discussion Points / Remarks
            </label>
            <textarea
              className="form-control"
              rows={5}
              value={entry.agenda}
              onChange={handleAgendaChange}
              placeholder="What was discussed / decided for this client..."
            />
          </Col>
        </Row>

        {showRemove && (
          <Button
            color="danger"
            size="sm"
            className="position-absolute top-0 end-0 mt-2 me-2"
            onClick={handleRemove}
          >
            Remove
          </Button>
        )}
      </div>
    )
  }
)

EntryRow.displayName = "EntryRow"

export default AddMeeting
